import React, { useEffect, useState, useRef } from 'react';
import { Plus, Edit2, Trash2, X, AlertCircle, Search, Building2, Eye, EyeOff, MessageCircle, Camera, Upload } from 'lucide-react';
import { getUsers, createUser, updateUser, deleteUser, getBagian, getRoles, getKaryawan, uploadUserPhoto } from '../api';
import { useAuth } from '../AuthContext';

const FALLBACK_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'staff', label: 'Staff' },
  { value: 'satgas', label: 'Satgas' },
  { value: 'operator', label: 'Operator' },
  { value: 'teknisi', label: 'Teknisi' },
];

const ROLE_COLORS = {
  admin: '#11cdef',
  manager: '#f59e0b',
  staff: '#2dce89',
  satgas: '#fb6340',
  operator: '#8965e0',
  teknisi: '#06b6d4',
};

const emptyForm = {
  username: '',
  password: '',
  full_name: '',
  role: 'staff',
  bagian_id: '',
  phone: '',
  email: '',
  telegram_chat_id: '',
};

export default function Users() {
  const { hasMenuAction } = useAuth();
  const [users, setUsers] = useState([]);
  const [bagianList, setBagianList] = useState([]);
  const [karyawanList, setKaryawanList] = useState([]);
  const [manualNama, setManualNama] = useState(false);
  const [roles, setRoles] = useState(FALLBACK_ROLES);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const loadUsers = () => {
    setLoading(true);
    Promise.all([getUsers(), getBagian(), getRoles(), getKaryawan()])
      .then(([usersRes, bagianRes, rolesRes, karyRes]) => {
        setUsers(usersRes.data);
        setBagianList(bagianRes.data);
        setKaryawanList(karyRes.data || []);
        if (rolesRes.data && rolesRes.data.length > 0) {
          setRoles(rolesRes.data.map(r => ({
            value: r.name,
            label: r.display_name,
          })));
        }
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const filteredUsers = users.filter(u =>
    !search || u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingUser(null);
    setForm({ ...emptyForm, password: '' });
    setPhotoPreview(null);
    setManualNama(false);
    setError('');
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      username: user.username,
      password: '',
      full_name: user.full_name || '',
      role: user.role,
      bagian_id: user.bagian_id ?? '',
      phone: user.phone || '',
      email: user.email || '',
      telegram_chat_id: user.telegram_chat_id || '',
    });
    setShowPassword(false);
    setPhotoPreview(user.foto_url || null);
    // Check if the user's name matches any karyawan
    const found = karyawanList.find(k => k.nama === (user.full_name || ''));
    setManualNama(!found);
    setError('');
    setShowModal(true);
  };

  const handlePhotoUpload = async (file) => {
    if (!file || !editingUser) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);

    // Upload
    setUploadingPhoto(true);
    try {
      const res = await uploadUserPhoto(file, editingUser.id);
      const newUrl = res.data?.url;
      if (newUrl) {
        setEditingUser(prev => ({ ...prev, foto_url: newUrl }));
      }
      loadUsers();
    } catch (err) {
      console.error('Upload foto gagal:', err);
    } finally {
      setUploadingPhoto(false);
    }
  };

  useEffect(() => {
    if (showCamera && cameraStream && videoRef.current) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [showCamera, cameraStream]);

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      setCameraStream(stream);
      setShowCamera(true);
    } catch (err) {
      alert('Tidak dapat mengakses kamera. Pastikan izin kamera sudah diberikan.');
      console.error('Camera error:', err);
    }
  };

  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
    }
    setCameraStream(null);
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // Mirror image (selfie)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to file
    canvas.toBlob(async (blob) => {
      if (!blob || !editingUser) return;
      const file = new File([blob], `selfie_${editingUser.id}.jpg`, { type: 'image/jpeg' });
      closeCamera();
      await handlePhotoUpload(file);
    }, 'image/jpeg', 0.85);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handlePhotoUpload(file);
  };

  const handleSave = async () => {
    setError('');
    if (!form.username) { setError('Username harus diisi'); return; }
    if (!editingUser && !form.password) { setError('Password harus diisi'); return; }

    setSaving(true);
    try {
      if (editingUser) {
        const payload = {
          full_name: form.full_name,
          role: form.role,
          bagian_id: form.bagian_id ? Number(form.bagian_id) : null,
          phone: form.phone,
          email: form.email,
          telegram_chat_id: form.telegram_chat_id,
          foto_url: editingUser.foto_url || '',
        };
        if (form.password) payload.password = form.password;
        await updateUser(editingUser.id, payload);
      } else {
        await createUser({
          ...form,
          bagian_id: form.bagian_id ? Number(form.bagian_id) : null,
        });
      }
      setShowModal(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menyimpan user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Yakin ingin menghapus user "${user.full_name || user.username}"?`)) return;
    try {
      await deleteUser(user.id);
      loadUsers();
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal menghapus user');
    }
  };

  const toggleActive = async (user) => {
    try {
      await updateUser(user.id, { is_active: !user.is_active });
      loadUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const getRoleLabel = (roleValue) => {
    const found = roles.find(r => r.value === roleValue);
    return found ? found.label : roleValue;
  };

  const getRoleColor = (roleValue) => {
    return ROLE_COLORS[roleValue] || '#6b7280';
  };

  return (
    <div className="page users-page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Management User</h2>
            <p className="page-desc">{users.length} user terdaftar dalam sistem</p>
          </div>
          {hasMenuAction('users', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} />
              <span>Tambah User</span>
            </button>
          )}
        </div>
      </div>

      <div className="filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Cari user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Memuat data user...</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Nama Lengkap</th>
                <th>Role</th>
                <th>Bagian</th>
                <th>Email</th>
                <th>Telepon</th>
                <th>Telegram ID</th>
                <th>Status</th>
                <th style={{ width: 120 }}>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u.id}>
                  <td><strong>{u.username}</strong></td>
                  <td>{u.full_name || '-'}</td>
                  <td>
                    <span className="role-badge" style={{
                      background: getRoleColor(u.role) + '18',
                      color: getRoleColor(u.role),
                    }}>
                      {getRoleLabel(u.role)}
                    </span>
                  </td>
                  <td>
                    {u.bagian_name ? (
                      <span className="cell-with-icon">
                        <Building2 size={14} className="text-muted" />
                        <span>{u.bagian_name}</span>
                      </span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>{u.email || '-'}</td>
                  <td>{u.phone || '-'}</td>
                  <td>{u.telegram_chat_id ? (
                    <span style={{ color: 'var(--success)', fontSize: '0.8rem' }}>
                      <MessageCircle size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {u.telegram_chat_id}
                    </span>
                  ) : (
                    <span className="text-muted">-</span>
                  )}</td>
                  <td>
                    {hasMenuAction('users', 'edit') ? (
                      <span
                        className={`status-toggle ${u.is_active ? 'active' : 'inactive'}`}
                        onClick={() => toggleActive(u)}
                      >
                        {u.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    ) : (
                      <span className={`status-toggle ${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="action-btns">
                      {hasMenuAction('users', 'edit') && (
                        <button className="btn-icon" title="Edit" onClick={() => openEdit(u)}>
                          <Edit2 size={14} />
                        </button>
                      )}
                      {hasMenuAction('users', 'delete') && (
                        <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(u)}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr><td colSpan={9} className="text-center text-muted">Tidak ada data user</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="modal-header" style={{
              background: editingUser ? 'linear-gradient(135deg, #5e72e4, #825ee4)' : 'linear-gradient(135deg, #11cdef, #0ea5e9)',
              color: '#fff', borderRadius: '12px 12px 0 0', padding: '14px 24px',
            }}>
              <div>
                <h3 style={{ color: '#fff', margin: 0, fontSize: '1rem' }}>
                  {editingUser ? '✏️ Edit User' : '👤 Tambah User Baru'}
                </h3>
                <small style={{ opacity: 0.85 }}>{editingUser ? `Memperbarui user ${editingUser.username}` : 'Buat akun baru untuk akses sistem'}</small>
              </div>
              <button className="btn-icon" onClick={() => setShowModal(false)} style={{ color: '#fff', background: 'rgba(255,255,255,0.15)', borderRadius: 8 }}>
                <X size={18} />
              </button>
            </div>

            <div className="modal-body" style={{ padding: '20px 24px', background: '#fff' }}>
              {error && (
                <div className="form-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, color: '#c0392b' }}>
                  <AlertCircle size={16} /><span>{error}</span>
                </div>
              )}

              {/* ===== INFORMASI AKUN ===== */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                borderBottom: '2px solid #11cdef', paddingBottom: 8, marginBottom: 16,
              }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: '#11cdef', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                </div>
                <div>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>Informasi Akun</h4>
                  <small style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>Username & password untuk login</small>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Username <span style={{ color: '#e74c3c' }}>*</span></label>
                  <input
                    type="text"
                    value={form.username}
                    onChange={e => setForm({ ...form, username: e.target.value })}
                    disabled={!!editingUser}
                    placeholder="cth: ahmad.syauqi"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', background: editingUser ? '#f8f9fa' : '#fff', boxSizing: 'border-box' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Password {editingUser && <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>(kosongkan jika tidak diubah)</span>} <span style={{ color: '#e74c3c' }}>*</span></label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => { setForm({ ...form, password: e.target.value }); }}
                      placeholder={editingUser ? 'Biarkan kosong' : 'Min. 6 karakter'}
                      autoComplete="new-password"
                      style={{ width: '100%', padding: '8px 40px 8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', boxSizing: 'border-box' }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--gray-400)', cursor: 'pointer', padding: 4 }}
                      tabIndex={-1}
                      title={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <small style={{ fontSize: '0.68rem', color: 'var(--gray-400)', marginTop: 2, display: 'block' }}>Minimal 6 karakter</small>
                </div>
              </div>

              {/* ===== DATA KARYAWAN / NAMA ===== */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                borderBottom: '2px solid #2dce89', paddingBottom: 8, marginBottom: 16,
              }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: '#2dce89', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>
                <div>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>Data Identitas</h4>
                  <small style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>Nama lengkap & role akses</small>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Nama Lengkap <span style={{ color: '#e74c3c' }}>*</span></label>
                  {manualNama || editingUser ? (
                    <div>
                      <input
                        type="text"
                        value={form.full_name}
                        onChange={e => setForm({ ...form, full_name: e.target.value })}
                        placeholder="Masukkan nama lengkap"
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', boxSizing: 'border-box' }}
                      />
                      {!editingUser && (
                        <div style={{ fontSize: '0.7rem', color: '#11cdef', cursor: 'pointer', marginTop: 4, display: 'flex', alignItems: 'center', gap: 3 }}
                          onClick={() => { setManualNama(false); setForm(f => ({ ...f, full_name: '' })); }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5m7-7-7 7 7 7"/></svg>
                          Pilih dari data karyawan
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <select
                        value={form.full_name}
                        onChange={e => {
                          const nama = e.target.value;
                          setForm({ ...form, full_name: nama });
                          if (nama) {
                            const k = karyawanList.find(kk => kk.nama === nama);
                            if (k) {
                              setForm(f => ({
                                ...f,
                                full_name: nama,
                                bagian_id: k.bagian_id ?? f.bagian_id,
                                phone: k.telepon || f.phone,
                                email: k.email || f.email,
                              }));
                            }
                          }
                        }}
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', background: karyawanList.length > 0 ? '#f0fdf4' : '#fff', boxSizing: 'border-box' }}
                      >
                        <option value="">-- Pilih Karyawan --</option>
                        {karyawanList.filter(k => k.status === 'Aktif').map(k => (
                          <option key={k.id} value={k.nama}>
                            {k.nama}{k.jabatan ? ` — ${k.jabatan}` : ''}{k.nik ? ` (${k.nik})` : ''}
                          </option>
                        ))}
                      </select>
                      <div style={{ fontSize: '0.7rem', color: '#f5365c', cursor: 'pointer', marginTop: 4, display: 'flex', alignItems: 'center', gap: 3 }}
                        onClick={() => { setManualNama(true); setForm(f => ({ ...f, full_name: '' })); }}>
                        Atau ketik manual
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14m-7-7 7 7-7 7"/></svg>
                      </div>
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Role Akses <span style={{ color: '#e74c3c' }}>*</span></label>
                  <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}>
                    {roles.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* ===== KONTAK & NOTIFIKASI ===== */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                borderBottom: '2px solid #fb6340', paddingBottom: 8, marginBottom: 16,
              }}>
                <div style={{ width: 28, height: 28, borderRadius: 6, background: '#fb6340', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                </div>
                <div>
                  <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--gray-700)', margin: 0 }}>Kontak & Notifikasi</h4>
                  <small style={{ color: 'var(--gray-400)', fontSize: '0.7rem' }}>Informasi kontak untuk notifikasi sistem</small>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm({ ...form, email: e.target.value })}
                    placeholder="email@contoh.com"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Bagian / Departemen</label>
                  <select value={form.bagian_id} onChange={e => setForm({ ...form, bagian_id: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}>
                    <option value="">-- Pilih Bagian --</option>
                    {bagianList.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>Telepon</label>
                  <input
                    type="text"
                    value={form.phone}
                    onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="08xxxxxxxxxx"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-600)', display: 'block', marginBottom: 4 }}>
                    <MessageCircle size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                    Telegram Chat ID
                  </label>
                  <input
                    type="text"
                    value={form.telegram_chat_id}
                    onChange={e => setForm({ ...form, telegram_chat_id: e.target.value })}
                    placeholder="123456789"
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--gray-200)', borderRadius: 8, fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                  <small style={{ fontSize: '0.68rem', color: 'var(--gray-400)', marginTop: 2, display: 'block' }}>Cek dengan @userinfobot di Telegram</small>
                </div>
              </div>

              {/* ===== FOTO PROFIL (edit only) ===== */}
              {editingUser && (
                <div style={{
                  marginTop: 16, padding: 16,
                  background: '#f8f9fa', borderRadius: 12,
                  border: '1px solid var(--gray-100)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 72, height: 72, borderRadius: '50%',
                      background: photoPreview ? 'transparent' : 'var(--gray-100)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden', border: '3px solid #fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      position: 'relative', flexShrink: 0,
                    }}>
                      {photoPreview ? (
                        <img src={photoPreview} alt="Foto" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Camera size={24} style={{ color: 'var(--gray-400)' }} />
                      )}
                      {uploadingPhoto && (
                        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span className="spinner" />
                        </div>
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <strong style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                        {photoPreview ? 'Foto Profil' : 'Belum ada foto profil'}
                      </strong>
                      <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', margin: '2px 0 6px' }}>
                        Pilih sumber foto:
                      </p>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="btn btn-primary" style={{ fontSize: '0.72rem', padding: '5px 10px', borderRadius: 6 }} onClick={openCamera}>
                          <Camera size={12} style={{ marginRight: 4 }} />Buka Kamera
                        </button>
                        <button type="button" className="btn btn-secondary" style={{ fontSize: '0.72rem', padding: '5px 10px', borderRadius: 6 }} onClick={() => fileInputRef.current?.click()}>
                          <Upload size={12} style={{ marginRight: 4 }} />Pilih File
                        </button>
                      </div>
                    </div>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>

            {/* Camera overlay */}
            {showCamera && (
              <div style={{
                position: 'fixed', inset: 0, zIndex: 2000,
                background: '#000', display: 'flex',
                flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: '100%', maxWidth: 400, maxHeight: '70vh',
                    transform: 'scaleX(-1)', borderRadius: 12,
                  }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                <div style={{ display: 'flex', gap: 16, marginTop: 20 }}>
                  <button className="btn btn-secondary" onClick={closeCamera}>
                    <X size={16} />
                    <span>Batal</span>
                  </button>
                  <button className="btn btn-primary" onClick={capturePhoto}>
                    <Camera size={16} />
                    <span>Ambil Foto</span>
                  </button>
                </div>
              </div>
            )}

            <div className="modal-footer" style={{
              padding: '12px 24px', borderTop: '1px solid var(--gray-100)',
              display: 'flex', justifyContent: 'flex-end', gap: 10,
              background: '#fafafa', borderRadius: '0 0 12px 12px',
            }}>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}
                style={{ background: editingUser ? 'linear-gradient(135deg, #5e72e4, #825ee4)' : 'linear-gradient(135deg, #11cdef, #0ea5e9)', border: 'none' }}>
                {saving ? 'Menyimpan...' : editingUser ? 'Perbarui User' : 'Simpan User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
